import { ApolloLink, Observable, type FetchResult } from '@apollo/client'
import { getMainDefinition } from '@apollo/client/utilities'

// A query that reads while a mutation is being saved can return the data from
// before it, and landing after the edit's optimistic cache write it puts the
// old value back on screen (the AI Workout Assistant: a moved exercise or
// workout jumped back, though the server had the move). A query that opts in
// with this context is fetched again, once the mutations it overlapped have
// settled, instead of delivering what may be the older data.
export const RETRY_IF_STALE_CONTEXT = { retryIfStale: true }

// Enough for a burst of edits; past it the last result is delivered.
const MAX_ATTEMPTS = 3

let clock = 0
let lastMutationStartedAt = 0
const mutationsInFlight = new Set<Promise<void>>()

// Only edits to a workout program count. The dashboard saves on a timer
// (the work heartbeat, experiments), and counting those would refetch the
// week every few seconds for nothing.
const isProgramEdit = operation => {
  const definition = getMainDefinition(operation.query)
  return (
    definition.kind === 'OperationDefinition' &&
    definition.operation === 'mutation' &&
    /WorkoutProgram/.test(operation.operationName ?? '')
  )
}

const trackMutation = (operation, forward) => {
  lastMutationStartedAt = ++clock
  let settle: () => void = () => undefined
  const settled = new Promise<void>(resolve => {
    settle = resolve
  })
  mutationsInFlight.add(settled)
  const finish = () => {
    if (!mutationsInFlight.delete(settled)) return
    settle()
  }
  return new Observable<FetchResult>(observer => {
    const subscription = forward(operation).subscribe({
      next: result => observer.next(result),
      error: error => {
        finish()
        observer.error(error)
      },
      complete: () => {
        finish()
        observer.complete()
      },
    })
    return () => {
      finish()
      subscription.unsubscribe()
    }
  })
}

const readFresh = (operation, forward) =>
  new Observable<FetchResult>(observer => {
    let subscription
    let closed = false
    let attempts = 0
    const run = () => {
      attempts += 1
      const startedAt = ++clock
      // A mutation already being saved may commit after this read.
      const overlapped = mutationsInFlight.size > 0
      let retrying = false
      let delivered = false
      subscription = forward(operation).subscribe({
        next: result => {
          const stale = overlapped || lastMutationStartedAt > startedAt
          if (stale && attempts < MAX_ATTEMPTS) {
            retrying = true
            Promise.all(Array.from(mutationsInFlight)).then(() => {
              if (!closed) run()
            })
            return
          }
          delivered = true
          observer.next(result)
        },
        error: error => {
          if (!retrying) observer.error(error)
        },
        complete: () => {
          if (delivered) observer.complete()
        },
      })
    }
    run()
    return () => {
      closed = true
      if (subscription) subscription.unsubscribe()
    }
  })

export const staleReadLink = new ApolloLink((operation, forward) => {
  if (isProgramEdit(operation)) return trackMutation(operation, forward)
  if (!operation.getContext().retryIfStale) return forward(operation)
  return readFresh(operation, forward)
})
